import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

// IKIGAI Path Finder - Enhanced Mobile-first React Prototype
// Life-purpose oriented with 6 nuanced personas + AI-style affirmation
// Integrated with Shakti Wallet (localStorage)
// Enhancements: Detailed persona score breakdown, normalized scoring for balance, More persona-specific questions, Framer Motion animations, Custom dark pink palette (Rose Garden), Progress bar, Colorful score bars

const STORAGE_KEY_WALLET = "sheearns_shakti_wallet_v1";
const STORAGE_KEY_IKIGAI = "sheearns_ikigai_v1";

const PERSONAS = [
  { id: "nurturer", title: "🌷 Nurturer", desc: "Empathy, care, harmony — you uplift others.", aff: "You bring warmth wherever you go — your compassion is your power." },
  { id: "visionary", title: "🔮 Visionary", desc: "Big ideas, leadership, change — you imagine the future.", aff: "Your dreams light the way — you see what others can’t yet imagine." },
  { id: "creator", title: "🎨 Creator", desc: "Expression, originality, making beauty — you craft meaning.", aff: "Your creativity colors the world — keep making it beautiful." },
  { id: "healer", title: "🌿 Healer", desc: "Balance, renewal, calm — you help restore peace.", aff: "You restore calm and inspire healing in others." },
  { id: "organizer", title: "⚙️ Organizer", desc: "Structure, clarity, systems — you bring order and impact.", aff: "You turn chaos into clarity — your discipline empowers many." },
  { id: "explorer", title: "🌍 Explorer", desc: "Curiosity, growth, adventure — you expand horizons.", aff: "You’re here to explore and expand — every step opens a new horizon." },
];

const PERSONA_COLORS = {
  nurturer: '#FFB6C1',    // Light Pink
  visionary: '#9370DB',   // Medium Purple
  creator: '#FFD700',     // Gold
  healer: '#90EE90',      // Light Green
  organizer: '#FFA500',   // Orange
  explorer: '#87CEEB',    // Sky Blue
};

const MAX_SCORES = {
  nurturer: 19,
  visionary: 18,
  creator: 13,
  healer: 13,
  organizer: 13,
  explorer: 12,
};

// Each question has options; each option adds points to one or more personas
const QUESTIONS = [
  {
    q: "When you wake up feeling most satisfied, what did you do?",
    opts: [
      { t: "Helped someone through a problem", adds: { nurturer: 2, healer: 1 } },
      { t: "Started a new idea or project", adds: { visionary: 2, explorer: 1 } },
      { t: "Made something beautiful or useful", adds: { creator: 2, organizer: 1 } },
    ],
  },
  {
    q: "What activity makes time disappear for you?",
    opts: [
      { t: "Listening and supporting others", adds: { nurturer: 2, healer: 1 } },
      { t: "Sketching, crafting or writing", adds: { creator: 2 } },
      { t: "Researching new skills or places", adds: { explorer: 2, visionary: 1 } },
    ],
  },
  {
    q: "Which contribution feels most meaningful to you?",
    opts: [
      { t: "Creating systems that help people", adds: { organizer: 2 } },
      { t: "Inspiring people with ideas", adds: { visionary: 2 } },
      { t: "Nurturing growth & wellbeing", adds: { healer: 2, nurturer: 1 } },
    ],
  },
  {
    q: "Your ideal weekend: pick one.",
    opts: [
      { t: "Volunteering at a community group", adds: { nurturer: 2, organizer: 1 } },
      { t: "Exploring a new town or workshop", adds: { explorer: 2, creator: 1 } },
      { t: "Designing a small project or artwork", adds: { creator: 2, visionary: 1 } },
    ],
  },
  {
    q: "What do you want your work to bring to others?",
    opts: [
      { t: "Comfort and care", adds: { nurturer: 2, healer: 1 } },
      { t: "Clarity and systems", adds: { organizer: 2 } },
      { t: "Inspiration or wonder", adds: { visionary: 2, explorer: 1 } },
    ],
  },
  {
    q: "What energizes you most in a conversation?",
    opts: [
      { t: "Sharing stories and emotional insights", adds: { nurturer: 2, healer: 1 } },
      { t: "Debating bold visions for the future", adds: { visionary: 2 } },
      { t: "Brainstorming creative solutions", adds: { creator: 2, organizer: 1 } },
    ],
  },
  {
    q: "When facing a challenge, what’s your go-to strength?",
    opts: [
      { t: "Empathizing and finding compassionate paths", adds: { nurturer: 2 } },
      { t: "Planning structured steps to overcome it", adds: { organizer: 2 } },
      { t: "Seeking new experiences or knowledge to adapt", adds: { explorer: 2, healer: 1 } },
    ],
  },
  {
    q: "What legacy do you hope to leave behind?",
    opts: [
      { t: "A community of supported and healed individuals", adds: { healer: 2, nurturer: 1 } },
      { t: "Innovative ideas that transform society", adds: { visionary: 2 } },
      { t: "Timeless creations that inspire generations", adds: { creator: 2, explorer: 1 } },
    ],
  },
  {
    q: "How do you recharge after a long day?",
    opts: [
      { t: "Spending time with loved ones, offering or receiving care", adds: { nurturer: 2, healer: 1 } },
      { t: "Journaling wild ideas and future plans", adds: { visionary: 2, explorer: 1 } },
      { t: "Organizing my space or creating a small craft", adds: { organizer: 2, creator: 1 } },
    ],
  },
  {
    q: "What book or media moves you the most?",
    opts: [
      { t: "Stories of personal growth and emotional healing", adds: { healer: 2, nurturer: 1 } },
      { t: "Biographies of leaders who sparked change", adds: { visionary: 2 } },
      { t: "Travelogues or artistic manifestos that ignite curiosity", adds: { explorer: 2, creator: 1 } },
    ],
  },
  {
    q: "In a team, what role do you naturally take?",
    opts: [
      { t: "The empathetic mediator fostering harmony", adds: { nurturer: 2 } },
      { t: "The strategist mapping out efficient processes", adds: { organizer: 2, healer: 1 } },
      { t: "The innovator pitching bold, creative directions", adds: { visionary: 2, creator: 1 } },
    ],
  },
];

export default function IkigaiPathFinder() {
  const [answers, setAnswers] = useState(() => QUESTIONS.map(() => null));
  const [pos, setPos] = useState(0);
  const [result, setResult] = useState(null);
  const [wallet, setWallet] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_WALLET);
      return raw ? JSON.parse(raw) : { coins: 0, totalXP: 0, peacePoints: 0, lastSync: null };
    } catch {
      return { coins: 0, totalXP: 0, peacePoints: 0, lastSync: null };
    }
  });

  useEffect(() => {
    // load any saved result
    try {
      const raw = localStorage.getItem(STORAGE_KEY_IKIGAI);
      if (raw) setResult(JSON.parse(raw));
    } catch {}
  }, []);

  const progress = ((pos + 1) / QUESTIONS.length) * 100;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const fadeInUpVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const choose = (idx) => {
    const copy = [...answers];
    copy[pos] = idx;
    setAnswers(copy);
    if (pos < QUESTIONS.length - 1) setPos(pos + 1);
    else finish(copy);
  };

  const finish = (finalAnswers) => {
    // tally scores
    const scores = PERSONAS.reduce((acc, p) => ({ ...acc, [p.id]: 0 }), {});
    finalAnswers.forEach((ansIdx, qIdx) => {
      if (ansIdx == null) return;
      const adds = QUESTIONS[qIdx].opts[ansIdx].adds;
      Object.keys(adds).forEach((k) => {
        scores[k] = (scores[k] || 0) + adds[k];
      });
    });

    // normalize scores for balanced comparison
    const normalized = {};
    let topNormalized = -Infinity;
    let top = PERSONAS[0];
    PERSONAS.forEach((p) => {
      const norm = (scores[p.id] || 0) / MAX_SCORES[p.id] * 100;
      normalized[p.id] = norm;
      if (norm > topNormalized) {
        topNormalized = norm;
        top = p;
      }
    });

    // craft result
    const xpEarned = 20;
    const coinsEarned = 10;
    const res = {
      topPersona: top,
      scores,
      normalized,
      xpEarned,
      coinsEarned,
      ts: new Date().toISOString(),
    };

    // update wallet
    const updated = { ...wallet, coins: wallet.coins + coinsEarned, totalXP: wallet.totalXP + xpEarned, lastSync: new Date().toISOString() };
    setWallet(updated);
    localStorage.setItem(STORAGE_KEY_WALLET, JSON.stringify(updated));
    localStorage.setItem(STORAGE_KEY_IKIGAI, JSON.stringify(res));
    setResult(res);
  };

  const replay = () => {
    setAnswers(QUESTIONS.map(() => null));
    setPos(0);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-800 via-gray-700 to-gray-800 p-2 sm:p-4 flex flex-col items-center text-white">
      <div className="w-full max-w-sm sm:max-w-md">
        <motion.h1
          className="text-lg sm:text-xl font-bold text-center mb-1"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          🌸 IKIGAI Path Finder
        </motion.h1>
        <motion.p
          className="text-xs sm:text-sm text-center text-white mb-2 sm:mb-4"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        >
          A gentle journey to discover your life-purpose persona.
        </motion.p>

        <AnimatePresence mode="wait">
          {!result ? (
            <motion.div
              key="quiz"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.3 } }}
              variants={containerVariants}
              className="bg-[#C71585]/30 backdrop-blur-sm rounded-2xl p-3 sm:p-4 shadow-xl border border-[#FF1493]/50"
            >
              {/* Progress Bar */}
              <motion.div
                className="w-full bg-[#FF1493]/20 rounded-full h-2 mb-4"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: progress / 100 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                <motion.div
                  className="bg-[#FF69B4] h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />
              </motion.div>
              <motion.div
                className="text-xs text-yellow-200 mb-2"
                variants={itemVariants}
              >
                Question {pos + 1} of {QUESTIONS.length}
              </motion.div>
              <motion.div
                className="text-base sm:text-lg font-semibold mb-3"
                variants={itemVariants}
              >
                {QUESTIONS[pos].q}
              </motion.div>
              <motion.div
                className="space-y-2 sm:space-y-3"
                variants={containerVariants}
              >
                {QUESTIONS[pos].opts.map((o, i) => (
                  <motion.button
                    key={i}
                    onClick={() => choose(i)}
                    className="w-full text-left p-3 rounded-xl border border-[#FF1493]/50 hover:bg-[#FF69B4]/20 focus:outline-none focus:ring-2 focus:ring-[#FF69B4]"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    {o.t}
                  </motion.button>
                ))}
              </motion.div>

              <motion.div
                className="mt-3 sm:mt-4 flex justify-between gap-4 text-xs text-[#FF69B4]"
                variants={itemVariants}
              >
                <motion.button
                  disabled={pos === 0}
                  onClick={() => setPos(Math.max(0, pos - 1))}
                  className="px-2 sm:px-3 py-1 rounded-md border border-[#FF1493]/50 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#FF69B4]/20"
                  whileHover={pos > 0 ? { scale: 1.05 } : {}}
                  whileTap={{ scale: 0.95 }}
                >
                  Back
                </motion.button>
                <motion.button
                  onClick={() => { setAnswers(QUESTIONS.map(() => null)); setPos(0); }}
                  className="px-2 sm:px-3 py-1 rounded-md border border-[#FF1493]/50 hover:bg-[#FF69B4]/20"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Restart
                </motion.button>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.5 }}
              className="bg-black backdrop-blur-sm rounded-2xl p-3 sm:p-4 shadow-xl border border-[#FF1493]/50 text-center"
            >
              <motion.div className="text-xs text-yellow-200" variants={itemVariants}>
                Result
              </motion.div>
              <div className="mt-2">
                <motion.div
                  className="text-xl sm:text-2xl font-bold"
                  variants={fadeInUpVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.1 }}
                >
                  {result.topPersona.title}
                </motion.div>
                <motion.div
                  className="text-xs sm:text-sm text-[#FF69B4] mt-2"
                  variants={fadeInUpVariants}
                  transition={{ delay: 0.2 }}
                >
                  {result.topPersona.desc}
                </motion.div>
                <motion.div
                  className="mt-3 p-3 bg-[#FF1493]/30 rounded-xl text-xs sm:text-sm border border-[#FF69B4]/50"
                  variants={fadeInUpVariants}
                  transition={{ delay: 0.3 }}
                >
                  🎁 You earned <span className="font-semibold text-white">{result.xpEarned} XP</span> and <span className="font-semibold text-white">{result.coinsEarned} Shakti Coins</span>
                </motion.div>
                <motion.div
                  className="mt-3 text-xs sm:text-sm italic text-white"
                  variants={fadeInUpVariants}
                  transition={{ delay: 0.4 }}
                >
                  "{result.topPersona.aff}"
                </motion.div>

                {/* Persona Score Breakdown */}
                <motion.div
                  className="mt-4 text-left"
                  variants={fadeInUpVariants}
                  transition={{ delay: 0.5 }}
                >
                  <h3 className="text-sm font-semibold mb-2 text-center">Your Persona Scores</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {PERSONAS.map((p) => {
                      const score = result.scores[p.id] || 0;
                      const norm = Math.round(result.normalized[p.id] || 0);
                      const width = `${norm}%`;
                      const barColor = PERSONA_COLORS[p.id];
                      return (
                        <div key={p.id} className="mb-2">
                          <div className="flex justify-between text-xs mb-1">
                            <span>{p.title}</span>
                            <span>{score} ({norm}%)</span>
                          </div>
                          <div className="bg-[#FF1493]/20 rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all duration-500"
                              style={{ width, backgroundColor: barColor }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>

                <motion.div
                  className="mt-4 flex gap-2"
                  variants={containerVariants}
                  transition={{ delay: 0.6 }}
                >
                  <motion.button
                    onClick={replay}
                    className="flex-1 py-2 rounded-xl border border-[#FF1493]/50 hover:bg-[#FF69B4]/20 text-white"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Try Again
                  </motion.button>
                  <motion.button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="flex-1 py-2 rounded-xl bg-[#FF69B4] hover:bg-[#FF1493] text-white focus:outline-none focus:ring-2 focus:ring-white"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Share
                  </motion.button>
                </motion.div>

                <motion.div
                  className="mt-3 text-xs text-white"
                  variants={fadeInUpVariants}
                  transition={{ delay: 0.7 }}
                >
                  Saved to your Shakti Wallet on {new Date(result.ts).toLocaleString()}
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.footer
          className="mt-4 sm:mt-6 text-xs text-white text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          Part of SheEarns • Results are private and saved locally.
        </motion.footer>
      </div>
    </div>
  );
}